import { Button, useToast } from '@citizen-walk/shared-ui';
import type {
  AiAnalysisBousaiResult,
  AiAnalysisWalkerResult,
  AiVerification,
  AiVerificationAnswerValue,
  AiVerificationQuestion,
  AiVerificationTemplate,
  PostPhotoMeta,
} from '@citizen-walk/shared-types';
import { isQuestionApplicable, ranksForQuestion } from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { useOutputFormat } from '../analysis';
import { MapView } from '../map';
import {
  GenericAiResultBlock,
  Photo,
  useAiCategoryMasters,
  usePhotoUrl,
  type PostMinimal,
} from '../posts';
import { useSubmitVerification } from './useSubmitVerification';

/**
 * AI 解析結果の検証 form (Sprint V、user 側初実装)。
 *
 * 6 種の question type widget (three_state / binary / five_scale / yes_no / comment / custom_choice) +
 * perPost + perClassification (rank ごと) + 自由記述 freeComment を 1 つの form で submit する。
 *
 * docs/04 §4-3, REQ-4.2.6 / REQ-4.3.20〜25。
 *
 * 旧 v1 `old/apps/user/src/components/VerificationForm.tsx` 相当 (docs/97 §3 L3)、
 * widget pattern + validation pattern は SurveyForm を踏襲。
 *
 * validation:
 *   - required 質問が空 → submit 時にエラー表示 + submit 抑制
 *   - perClassificationQuestions は appliesToRanks で出題対象 rank を絞り込み、各 rank ごとに
 *     enabled な質問のみ表示 (post の classifications がない rank はスキップ)
 */

export interface VerificationFormPost {
  id: string;
  title: string;
  /** 投稿者 UID (回答 doc に写す + 「自分の投稿か」判定用)。旧 doc で欠けていれば null。 */
  authorUid: string | null;
  comment: string | null;
  aiAnalysisAnalyzedAt: string | null;
  aiAnalysisSettingsId: string | null;
  aiAnalysisEcw: PostMinimal['aiAnalysisEcw'];
  aiAnalysisBousai: AiAnalysisBousaiResult | null;
  aiAnalysisWalker: AiAnalysisWalkerResult | null;
  /**
   * MD-4 モードドリブン化 (2026-07-24): fields[] 駆動の汎用解析結果 (aiAnalysis.generic)。
   * 新規地区は mode 別 result を持たないため、検証はこのフィールドを読む。
   */
  aiAnalysisGeneric: Record<string, unknown> | null;
  // Phase G (old2 逐語): 「何を検証しているか」を見せるための投稿表示用 field。
  /** 投稿写真 (Storage path)。null は写真なし。 */
  photoPath: string | null;
  photoThumbPath: string | null;
  /** doc 保存済 download URL (楽観表示 / オフライン fallback、docs/106)。 */
  photoUrl: string | null;
  photoThumbUrl: string | null;
  /** 写真の幾何メタ (docs/27、360度写真は見回し表示)。旧投稿は null。 */
  photo?: PostPhotoMeta | null;
  /** 投稿位置 (場所の文脈把握用の地図表示)。null は位置なし。 */
  location: { latitude: number; longitude: number } | null;
}

export interface VerificationFormProps {
  districtId: string;
  uid: string;
  /**
   * R-70: 現 UID + 端末引き継ぎ元 UID 群。検証対象が「自分の投稿か」(isOwnPost) の判定に使う。
   * 未指定なら [uid] のみで判定。
   */
  selfUids?: string[];
  post: VerificationFormPost;
  template: AiVerificationTemplate;
  /** 配分セット経由なら ID、free verification なら null */
  assignmentSetId: string | null;
  existingVerification: AiVerification | null;
  /** submit 完了後の callback (modal 閉じる等)。 */
  onSubmitted: () => void;
  /** キャンセル callback。 */
  onCancel: () => void;
  /**
   * Phase G (old2 逐語): 連続回答。配分セット経由のとき、自動遷移先 (次の未回答投稿 ID) を解決する。
   * 戻り値 null = このセットの全投稿が完了済 → 末尾完了 alert を出して onSubmitted。
   * 未指定 (free verification 等) のとき連続回答は無効、従来どおり onSubmitted のみ。
   */
  findNextUnansweredPostId?: (currentPostId: string) => Promise<string | null>;
  /** 連続回答で次の投稿に遷移する callback (form を次 post に差し替える)。 */
  onAdvanceToPost?: (nextPostId: string) => void;
  /**
   * 投稿の文脈 (見出し / 写真 / 地図 / AI 解析結果) を form 内に表示するか (既定 true)。
   *
   * 2026-09-05 オーナー指示: 投稿詳細モーダルに埋め込むときは、投稿内容と AI 解析結果を
   * モーダル本体が既に出しているため二重表示になる。false にすると質問と送信操作だけを描画する。
   * 「AI 判定をチェック」からの単独表示 (何を検証しているか分からない) では true のまま。
   */
  showPostContext?: boolean;
}

// ============== 内部 form state 表現 ==============
//
// 各 question type ごとに緩い中間 form value を持ち、submit 時に AiVerificationAnswerValue に変換する。

type FormValue =
  | { kind: 'three_state'; value: 'valid' | 'neutral' | 'invalid' | '' }
  | { kind: 'binary'; value: 'valid' | 'invalid' | '' }
  | { kind: 'five_scale'; raw: string }
  | { kind: 'yes_no'; value: 'yes' | 'no' | '' }
  | { kind: 'comment'; value: string }
  | { kind: 'custom_choice'; values: string[]; other: string };

type FormValues = Record<string, FormValue>;

const OTHER_SENTINEL = '__other__';

/** Question の type に応じて初期値を作る。既存回答があれば pre-fill。 */
function initialValueOf(
  q: AiVerificationQuestion,
  existing: AiVerificationAnswerValue | undefined,
): FormValue {
  switch (q.type) {
    case 'three_state': {
      if (existing && existing.type === 'three_state') {
        return { kind: 'three_state', value: existing.value };
      }
      return { kind: 'three_state', value: '' };
    }
    case 'binary': {
      if (existing && existing.type === 'binary') {
        return { kind: 'binary', value: existing.value };
      }
      return { kind: 'binary', value: '' };
    }
    case 'five_scale': {
      if (existing && existing.type === 'five_scale') {
        return { kind: 'five_scale', raw: String(existing.value) };
      }
      return { kind: 'five_scale', raw: '' };
    }
    case 'yes_no': {
      if (existing && existing.type === 'yes_no') {
        return { kind: 'yes_no', value: existing.value ? 'yes' : 'no' };
      }
      return { kind: 'yes_no', value: '' };
    }
    case 'comment': {
      if (existing && existing.type === 'comment') {
        return { kind: 'comment', value: existing.value };
      }
      return { kind: 'comment', value: '' };
    }
    case 'custom_choice': {
      if (existing && existing.type === 'custom_choice') {
        return {
          kind: 'custom_choice',
          values: existing.value,
          other: existing.other ?? '',
        };
      }
      return { kind: 'custom_choice', values: [], other: '' };
    }
  }
}

function modeResultOf(post: VerificationFormPost): Record<string, unknown> | null {
  // 【廃止予定 fallback】ecw/bousai/walker の mode 別 result は legacyModeResults 地区の投稿のみ。
  // 既存地区の挙動保証のため先に判定し、新規地区 (generic のみ) は最後の分岐で読む。
  if (post.aiAnalysisEcw) return post.aiAnalysisEcw as unknown as Record<string, unknown>;
  if (post.aiAnalysisBousai) {
    return post.aiAnalysisBousai as unknown as Record<string, unknown>;
  }
  if (post.aiAnalysisWalker) {
    return post.aiAnalysisWalker as unknown as Record<string, unknown>;
  }
  // MD-4 モードドリブン化: fields[] 駆動の汎用結果 (新規地区の唯一の解析結果)。
  if (post.aiAnalysisGeneric && Object.keys(post.aiAnalysisGeneric).length > 0) {
    return post.aiAnalysisGeneric;
  }
  return null;
}

/** 分類ごと質問の対象になる 1 行 (mode 別 / generic 共通の最小形)。 */
export interface VerificationClassificationRow {
  rank: number;
  category_name?: string;
  category_code?: string;
  /** その行の評価 (例: ゲール基準の ポジティブ / ネガティブ / 中立)。見出しに添えて判断材料にする */
  assessment?: string;
}

/**
 * 分類行の「名前 / コード」列を M3 fields[] 駆動の列名ゆれから拾う (pure)。
 *
 * `category_name` / `category_code` (慣習キー) を最優先。無ければ `<prefix>_name` と `<prefix>_code` が
 * **対になっている prefix** (例: gql_name / gql_code) を優先し、対が無いときだけ最初の `*_name` / `*_code`
 * に落とす。無関係な `*_name` (例: sub_name) が先に並んでいても分類軸を取り違えないようにするため。
 */
export function pickClassificationLabel(o: Record<string, unknown>): {
  name?: string;
  code?: string;
} {
  const str = (k: string): string | undefined =>
    typeof o[k] === 'string' && (o[k] as string) ? (o[k] as string) : undefined;
  const catName = str('category_name');
  const catCode = str('category_code');
  if (catName || catCode) {
    return { ...(catName ? { name: catName } : {}), ...(catCode ? { code: catCode } : {}) };
  }
  const nameKeys = Object.keys(o).filter((k) => k.endsWith('_name') && str(k));
  const codeKeys = Object.keys(o).filter((k) => k.endsWith('_code') && str(k));
  for (const nk of nameKeys) {
    const prefix = nk.slice(0, -'_name'.length);
    const ck = `${prefix}_code`;
    if (codeKeys.includes(ck)) return { name: str(nk)!, code: str(ck)! };
  }
  const name = nameKeys[0] ? str(nameKeys[0]) : undefined;
  const code = codeKeys[0] ? str(codeKeys[0]) : undefined;
  return { ...(name ? { name } : {}), ...(code ? { code } : {}) };
}

/**
 * generic.classifications の 1 行を検証用の行に narrow する (pure、テスト用に export)。
 *
 * 慣習キー `category_name` / `category_code` を優先し、無ければ M3 出力フォーマットが定める別名
 * (例: ECW＋ウォーカブルの `gql_name` / `gql_code`) を `*_name` / `*_code` の suffix で拾う。
 * これが無いとゲール基準の行が 0 件扱いになり、分類ごとの質問が一切出なかった (2026-09-04)。
 */
export function asGenericClassification(
  v: unknown,
  index: number,
): VerificationClassificationRow | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const rank = typeof o.rank === 'number' && Number.isFinite(o.rank) ? o.rank : index + 1;
  const { name, code } = pickClassificationLabel(o);
  if (!name && !code) return null;
  const assessment = typeof o.assessment === 'string' && o.assessment ? o.assessment : undefined;
  return {
    rank,
    ...(name ? { category_name: name } : {}),
    ...(code ? { category_code: code } : {}),
    ...(assessment ? { assessment } : {}),
  };
}

function classificationsOf(post: VerificationFormPost): VerificationClassificationRow[] {
  // 【廃止予定 fallback】bousai/walker の mode 別 classifications は legacy 地区の投稿のみ。
  if (post.aiAnalysisBousai) {
    return post.aiAnalysisBousai.classifications.map((c) => ({
      rank: c.rank,
      category_name: c.category_name,
      category_code: c.category_code,
    }));
  }
  if (post.aiAnalysisWalker) {
    return post.aiAnalysisWalker.classifications.map((c) => ({
      rank: c.rank,
      category_name: c.category_name,
      category_code: c.category_code,
      ...(c.assessment ? { assessment: c.assessment } : {}),
    }));
  }
  // MD-4 モードドリブン化: generic.classifications (M3 fields[] の object-array) を読む。
  // per-classification 質問 (分類ごと検証) が新規 custom mode 地区でも機能する。
  const genericCls = post.aiAnalysisGeneric?.classifications;
  if (Array.isArray(genericCls)) {
    return genericCls
      .map((c, i) => asGenericClassification(c, i))
      .filter((c): c is VerificationClassificationRow => c !== null);
  }
  return [];
}

/** form value → AiVerificationAnswerValue (validation pass 後に呼ぶ、不正は null)。 */
function toAnswerValue(v: FormValue): AiVerificationAnswerValue | null {
  switch (v.kind) {
    case 'three_state':
      if (v.value === '') return null;
      return { type: 'three_state', value: v.value };
    case 'binary':
      if (v.value === '') return null;
      return { type: 'binary', value: v.value };
    case 'five_scale': {
      if (v.raw === '') return null;
      const n = Number(v.raw);
      if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5) {
        return { type: 'five_scale', value: n };
      }
      return null;
    }
    case 'yes_no':
      if (v.value === '') return null;
      return { type: 'yes_no', value: v.value === 'yes' };
    case 'comment':
      if (v.value === '') return null;
      return { type: 'comment', value: v.value };
    case 'custom_choice': {
      if (v.values.length === 0) return null;
      const includesOther = v.values.includes(OTHER_SENTINEL);
      const out: AiVerificationAnswerValue = {
        type: 'custom_choice',
        value: v.values,
      };
      if (includesOther) out.other = v.other;
      return out;
    }
  }
}

/** 単一 question の validation (pure、test 用に export)。 */
export function validateAnswer(q: AiVerificationQuestion, v: FormValue | undefined): string | null {
  if (!v) return q.required ? '必須項目です' : null;
  switch (v.kind) {
    case 'three_state':
    case 'binary':
    case 'yes_no':
      if (q.required && v.value === '') return '必須項目です';
      return null;
    case 'five_scale':
      if (q.required && v.raw === '') return '必須項目です';
      return null;
    case 'comment':
      if (q.required && v.value.trim() === '') return '必須項目です';
      return null;
    case 'custom_choice': {
      if (q.required && v.values.length === 0) return '必須項目です';
      if (
        q.allowOther &&
        v.values.includes(OTHER_SENTINEL) &&
        q.required &&
        v.other.trim() === ''
      ) {
        return '「その他」の内容を入力してください';
      }
      return null;
    }
  }
}

// ============== Question widgets ==============

interface WidgetProps {
  question: AiVerificationQuestion;
  value: FormValue;
  onChange: (next: FormValue) => void;
  disabled: boolean;
  /** form 内で id ユニーク化 (perClassification は同 qid が複数 rank で出るため) */
  fieldNamePrefix: string;
}

function ThreeStateWidget({ value, onChange, disabled, fieldNamePrefix }: WidgetProps) {
  const v = value.kind === 'three_state' ? value.value : '';
  const name = `${fieldNamePrefix}-three-state`;
  return (
    <div className="flex flex-wrap gap-3">
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'valid'}
          onChange={() => onChange({ kind: 'three_state', value: 'valid' })}
          disabled={disabled}
        />
        <span>✓ 妥当</span>
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'neutral'}
          onChange={() => onChange({ kind: 'three_state', value: 'neutral' })}
          disabled={disabled}
        />
        {/* Phase G: 表示ラベルのみ old2 逐語に復元。保存 value は 'neutral' 据え置き (分析集計を壊さない)。 */}
        <span>─ どちらでもない</span>
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'invalid'}
          onChange={() => onChange({ kind: 'three_state', value: 'invalid' })}
          disabled={disabled}
        />
        {/* Phase G: 表示ラベルのみ old2 逐語に復元。保存 value は 'invalid' 据え置き。 */}
        <span>✗ 妥当ではない</span>
      </label>
    </div>
  );
}

function BinaryWidget({ value, onChange, disabled, fieldNamePrefix }: WidgetProps) {
  const v = value.kind === 'binary' ? value.value : '';
  const name = `${fieldNamePrefix}-binary`;
  return (
    <div className="flex flex-wrap gap-3">
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'valid'}
          onChange={() => onChange({ kind: 'binary', value: 'valid' })}
          disabled={disabled}
        />
        <span>✓ 妥当</span>
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'invalid'}
          onChange={() => onChange({ kind: 'binary', value: 'invalid' })}
          disabled={disabled}
        />
        {/* Phase G: 表示ラベルのみ old2 逐語に復元。保存 value は 'invalid' 据え置き。 */}
        <span>✗ 妥当ではない</span>
      </label>
    </div>
  );
}

function FiveScaleWidget({ value, onChange, disabled, fieldNamePrefix }: WidgetProps) {
  const raw = value.kind === 'five_scale' ? value.raw : '';
  const name = `${fieldNamePrefix}-five-scale`;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-slate-500">低</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <label key={n} className="flex items-center gap-1 cursor-pointer text-sm">
          <input
            type="radio"
            name={name}
            checked={raw === String(n)}
            onChange={() => onChange({ kind: 'five_scale', raw: String(n) })}
            disabled={disabled}
          />
          <span>{n}</span>
        </label>
      ))}
      <span className="text-[11px] text-slate-500">高</span>
    </div>
  );
}

function YesNoWidget({ value, onChange, disabled, fieldNamePrefix }: WidgetProps) {
  const v = value.kind === 'yes_no' ? value.value : '';
  const name = `${fieldNamePrefix}-yes-no`;
  return (
    <div className="flex flex-wrap gap-3">
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'yes'}
          onChange={() => onChange({ kind: 'yes_no', value: 'yes' })}
          disabled={disabled}
        />
        <span>はい</span>
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
        <input
          type="radio"
          name={name}
          checked={v === 'no'}
          onChange={() => onChange({ kind: 'yes_no', value: 'no' })}
          disabled={disabled}
        />
        <span>いいえ</span>
      </label>
    </div>
  );
}

function CommentWidget({ value, onChange, disabled }: WidgetProps) {
  const v = value.kind === 'comment' ? value.value : '';
  return (
    <textarea
      value={v}
      onChange={(e) => onChange({ kind: 'comment', value: e.target.value })}
      maxLength={500}
      rows={3}
      disabled={disabled}
      // Phase G (old2 逐語): 個人情報注意の placeholder。
      placeholder="ご意見・ご感想をご記入ください（個人情報は書かないでください）"
      className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
    />
  );
}

function CustomChoiceWidget({ question, value, onChange, disabled, fieldNamePrefix }: WidgetProps) {
  const values = value.kind === 'custom_choice' ? value.values : [];
  const otherText = value.kind === 'custom_choice' ? value.other : '';
  const choices = question.choices ?? [];
  const allowMultiple = question.allowMultiple ?? false;
  const allowOther = question.allowOther ?? false;
  const name = `${fieldNamePrefix}-custom`;

  function choiceValue(idx: number): string {
    const c = choices[idx];
    if (c?.value) return c.value;
    return c?.label ?? `choice-${idx}`;
  }

  function toggleOne(val: string) {
    if (allowMultiple) {
      const next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val];
      onChange({ kind: 'custom_choice', values: next, other: otherText });
    } else {
      // radio: 単一選択
      onChange({ kind: 'custom_choice', values: [val], other: otherText });
    }
  }

  return (
    <div className="grid gap-1.5">
      {choices.map((c, idx) => {
        const val = choiceValue(idx);
        const checked = values.includes(val);
        return (
          <label key={`${val}-${idx}`} className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type={allowMultiple ? 'checkbox' : 'radio'}
              name={name}
              checked={checked}
              onChange={() => toggleOne(val)}
              disabled={disabled}
            />
            <span>{c.label}</span>
          </label>
        );
      })}
      {allowOther && (
        <>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type={allowMultiple ? 'checkbox' : 'radio'}
              name={name}
              checked={values.includes(OTHER_SENTINEL)}
              onChange={() => toggleOne(OTHER_SENTINEL)}
              disabled={disabled}
            />
            <span>その他</span>
          </label>
          {values.includes(OTHER_SENTINEL) && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => onChange({ kind: 'custom_choice', values, other: e.target.value })}
              // Phase G (old2 逐語): 個人情報注意を含む placeholder。aria-label は既存の a11y を維持。
              placeholder="その他の内容（個人情報は書かないでください）"
              disabled={disabled}
              aria-label="その他の内容"
              className="ml-6 min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          )}
        </>
      )}
    </div>
  );
}

function QuestionWidget(props: WidgetProps) {
  switch (props.question.type) {
    case 'three_state':
      return <ThreeStateWidget {...props} />;
    case 'binary':
      return <BinaryWidget {...props} />;
    case 'five_scale':
      return <FiveScaleWidget {...props} />;
    case 'yes_no':
      return <YesNoWidget {...props} />;
    case 'comment':
      return <CommentWidget {...props} />;
    case 'custom_choice':
      return <CustomChoiceWidget {...props} />;
  }
}

// ============== 投稿コンテキスト表示 (写真 + 投稿位置の地図) ==============

/**
 * Phase G (old2 VerificationFormPage L244-265 逐語相当): 「何を検証しているか」を見せるための
 * 投稿写真 + 投稿位置の地図。AI 解析結果は別途 `AiResultSummary` が表示する。
 *
 * old2 との差分:
 *   - old2 は専用 `PostLocationMap` (usePmtilesMap 依存) を使用 → v2 は features/map の
 *     `MapView` を単一 post で再利用 (PMTiles cache / マーカー描画の重複を避ける)。
 *   - old2 はルートページの 2 カラム左 sticky 配置 → v2 は modal 内の上部ブロックに配置。
 */
function PostContextView({ post, districtId }: { post: VerificationFormPost; districtId: string }) {
  const photo = usePhotoUrl(post.photoPath, post.photoUrl);
  const photoThumb = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl);
  const photoUrl = photo.status === 'ready' ? photo.url : null;
  const photoThumbUrl = photoThumb.status === 'ready' ? photoThumb.url : null;
  const hasPhoto = post.photoPath !== null;
  const hasLocation = post.location !== null;
  if (!hasPhoto && !hasLocation) return null;

  // MapView は PostMinimal[] を取るため、最小 stub に投稿位置だけ載せて 1 件描画する。
  const mapPost = post.location
    ? ([
        {
          id: post.id,
          authorUid: '',
          authorNickname: '',
          title: post.title,
          comment: null,
          location: post.location,
          accuracy: null,
          source: null,
          visibility: 'public',
          sessionId: null,
          createdAt: null,
          photoPath: null,
          photoThumbPath: null,
          photoUrl: null,
          photoThumbUrl: null,
          agreeCount: 0,
          aiAnalysisStatus: null,
          aiAnalysisError: null,
          aiAnalysisEcw: null,
          aiAnalysisBousai: null,
          aiAnalysisWalker: null,
          aiAnalysisRaw: null,
          aiAnalysisAnalyzedAt: null,
          aiAnalysisSettingsId: null,
          aiAnalysisGeneric: null,
          extraFields: null,
        },
      ] as unknown as PostMinimal[])
    : [];

  return (
    <section className="grid gap-2">
      {hasPhoto && (photoUrl || photoThumbUrl) && (
        <Photo
          src={photoUrl}
          thumbSrc={photoThumbUrl}
          alt={`${post.title} の写真`}
          className="w-full max-h-[40vh] object-contain rounded-lg border border-slate-200 bg-slate-50"
          projection={post.photo?.projection ?? null}
          baseHeadingDeg={post.photo?.poseHeadingDeg ?? null}
        />
      )}
      {hasPhoto && !photoUrl && !photoThumbUrl && photo.status === 'loading' && (
        <p className="text-sm text-slate-500 text-center py-4">写真を読込中…</p>
      )}
      {hasLocation && post.location && (
        <div>
          <div className="text-xs text-slate-500 mb-1">📍 投稿位置</div>
          <div className="h-[180px] rounded border border-slate-300 overflow-hidden">
            <MapView
              posts={mapPost}
              districtId={districtId}
              centerOverride={{ lat: post.location.latitude, lng: post.location.longitude }}
              zoomOverride={16}
              showUserLocation={false}
            />
          </div>
        </div>
      )}
    </section>
  );
}

// ============== AI 結果 simple display ==============

function AiResultSummary({ post }: { post: VerificationFormPost }) {
  if (post.aiAnalysisEcw) {
    const ecw = post.aiAnalysisEcw;
    return (
      <section className="mb-3 p-3 rounded-md bg-emerald-50/50 border border-emerald-200 text-xs">
        <h4 className="text-emerald-900 font-semibold mb-1">🤖 AI 解析結果 (ECW)</h4>
        {ecw.description && <p className="text-slate-700">{ecw.description}</p>}
        {ecw.emotions.length > 0 && (
          <p className="text-slate-600 mt-1">感情: {ecw.emotions.join(' / ')}</p>
        )}
        {ecw.categoryCodes.length > 0 && (
          <p className="text-slate-600">カテゴリ: {ecw.categoryCodes.join(' / ')}</p>
        )}
      </section>
    );
  }
  if (post.aiAnalysisBousai) {
    const b = post.aiAnalysisBousai;
    return (
      <section className="mb-3 p-3 rounded-md bg-amber-50/50 border border-amber-200 text-xs">
        <h4 className="text-amber-900 font-semibold mb-1">🤖 AI 解析結果 (防災)</h4>
        {b.description && <p className="text-slate-700">{b.description}</p>}
        {b.classifications.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {b.classifications.map((c, idx) => (
              <li key={`${c.rank}-${idx}`} className="text-slate-600">
                #{c.rank} {c.category_name ?? c.category_code}
                {c.assessment && ` [${c.assessment}]`}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }
  if (post.aiAnalysisWalker) {
    const w = post.aiAnalysisWalker;
    return (
      <section className="mb-3 p-3 rounded-md bg-sky-50/50 border border-sky-200 text-xs">
        <h4 className="text-sky-900 font-semibold mb-1">🤖 AI 解析結果 (Walker)</h4>
        {w.description && <p className="text-slate-700">{w.description}</p>}
        {w.classifications.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {w.classifications.map((c, idx) => (
              <li key={`${c.rank}-${idx}`} className="text-slate-600">
                #{c.rank} {c.category_name ?? c.category_code}
                {c.assessment && ` [${c.assessment}]`}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }
  // MD-4 モードドリブン化: fields[] 駆動の汎用結果 (新規地区)。出力フォーマット (M3) の fields[] に
  // 沿って全項目 (説明 / 感情 / 対象物分類コード / 分類テーブル / 管理者が足した項目) を表示する。
  // 検証テンプレが「感情」「対象物分類」の妥当性を問うため、description と classifications だけの
  // 簡易表示では判断材料が足りない (2026-09-04)。
  if (post.aiAnalysisGeneric && genericHasValue(post.aiAnalysisGeneric)) {
    return <GenericAiResultSection post={post} generic={post.aiAnalysisGeneric} />;
  }
  return <p className="text-xs text-slate-500 mb-2">この投稿には AI 解析結果がまだありません。</p>;
}

/** generic に「表示する値」が 1 つでもあるか (空文字 / 空配列 / 空 object だけなら false)。 */
function genericHasValue(generic: Record<string, unknown>): boolean {
  return Object.values(generic).some((v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });
}

/**
 * generic (fields[] 駆動) の主表示。投稿詳細 (PostDetailModal) と同じ GenericAiResultBlock を
 * primary で使い、M3 出力フォーマットの schema (項目の並び・日本語ラベル・分類テーブルの動的列) と
 * M2 マスタ (感情 / 対象物分類コードの日本語名・配色 chip) を解決して渡す。
 * hook はこの子コンポーネント内に閉じるので、legacy (ecw/bousai/walker) 表示の投稿では動かない。
 */
function GenericAiResultSection({
  post,
  generic,
}: {
  post: VerificationFormPost;
  generic: Record<string, unknown>;
}) {
  const { format } = useOutputFormat(post.aiAnalysisSettingsId);
  const categoryMasters = useAiCategoryMasters(post.aiAnalysisSettingsId);
  return (
    <GenericAiResultBlock
      generic={generic}
      schema={format?.schema ?? null}
      primary
      resolver={categoryMasters}
    />
  );
}

// ============== Main component ==============

export function VerificationForm({
  districtId,
  uid,
  selfUids,
  post,
  template,
  assignmentSetId,
  existingVerification,
  onSubmitted,
  onCancel,
  findNextUnansweredPostId,
  onAdvanceToPost,
  showPostContext = true,
}: VerificationFormProps) {
  const { toast } = useToast();
  const { submit, state: submitState } = useSubmitVerification(districtId, post.id, uid);

  const modeResult = useMemo(() => modeResultOf(post), [post]);
  const classifications = useMemo(() => classificationsOf(post), [post]);
  const totalRanks = classifications.length;

  // 表示対象の perPost / perClassification 質問群を絞り込み (enabled + isQuestionApplicable)。
  const perPostVisible = useMemo(
    () => template.perPostQuestions.filter((q) => isQuestionApplicable(q, modeResult)),
    [template.perPostQuestions, modeResult],
  );
  const perClsVisibleByRank = useMemo(() => {
    const out: Record<number, AiVerificationQuestion[]> = {};
    if (totalRanks === 0) return out;
    for (const q of template.perClassificationQuestions) {
      if (!isQuestionApplicable(q, modeResult)) continue;
      const ranks = ranksForQuestion(q, totalRanks);
      for (const r of ranks) {
        if (!out[r]) out[r] = [];
        out[r].push(q);
      }
    }
    return out;
  }, [template.perClassificationQuestions, modeResult, totalRanks]);

  // 内部 form state を初期化 (既存 verification があれば pre-fill)。
  const [perPostValues, setPerPostValues] = useState<FormValues>(() => {
    const out: FormValues = {};
    for (const q of perPostVisible) {
      out[q.id] = initialValueOf(q, existingVerification?.perPostAnswers[q.id]);
    }
    return out;
  });
  const [perClsValues, setPerClsValues] = useState<Record<string, FormValues>>(() => {
    const out: Record<string, FormValues> = {};
    for (const [rank, qs] of Object.entries(perClsVisibleByRank)) {
      const rankAnswers = existingVerification?.perClassificationAnswers[rank] ?? {};
      const rankValues: FormValues = {};
      for (const q of qs) {
        rankValues[q.id] = initialValueOf(q, rankAnswers[q.id]);
      }
      out[rank] = rankValues;
    }
    return out;
  });
  const [freeComment, setFreeComment] = useState<string>(existingVerification?.freeComment ?? '');
  const [showErrors, setShowErrors] = useState(false);

  function setPerPost(qid: string, next: FormValue) {
    setPerPostValues((prev) => ({ ...prev, [qid]: next }));
  }
  function setPerCls(rank: string, qid: string, next: FormValue) {
    setPerClsValues((prev) => ({
      ...prev,
      [rank]: { ...(prev[rank] ?? {}), [qid]: next },
    }));
  }

  const perPostErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    for (const q of perPostVisible) {
      const e = validateAnswer(q, perPostValues[q.id]);
      if (e) errs[q.id] = e;
    }
    return errs;
  }, [perPostVisible, perPostValues]);

  const perClsErrors = useMemo(() => {
    const errs: Record<string, Record<string, string>> = {};
    for (const [rank, qs] of Object.entries(perClsVisibleByRank)) {
      const rankValues = perClsValues[rank] ?? {};
      const rankErrs: Record<string, string> = {};
      for (const q of qs) {
        const e = validateAnswer(q, rankValues[q.id]);
        if (e) rankErrs[q.id] = e;
      }
      if (Object.keys(rankErrs).length > 0) errs[rank] = rankErrs;
    }
    return errs;
  }, [perClsVisibleByRank, perClsValues]);

  const isSubmitting = submitState.status === 'submitting';

  /**
   * Phase G (old2 逐語): 必須未回答の件数を数える。
   *
   * old2 は required 違反を「hard block」せず「{n}件の必須質問が未回答です。送信しますか？」の
   * confirm で部分保存を許可する (completed フラグで完了/下書きを区別)。
   * v2 の旧挙動 (hard block) からこの soft confirm 方式に揃える。
   */
  function countRequiredMissing(): number {
    let missing = 0;
    for (const q of perPostVisible) {
      if (!q.required || !q.enabled) continue;
      if (validateAnswer(q, perPostValues[q.id])) missing += 1;
    }
    for (const [rank, qs] of Object.entries(perClsVisibleByRank)) {
      const rankValues = perClsValues[rank] ?? {};
      for (const q of qs) {
        if (!q.required || !q.enabled) continue;
        if (validateAnswer(q, rankValues[q.id])) missing += 1;
      }
    }
    return missing;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // custom_choice「その他」未入力など、required 以外の format エラーは従来どおり hard block。
    const formatErrors =
      Object.values(perPostErrors).some((m) => m !== '必須項目です') ||
      Object.values(perClsErrors).some((rank) =>
        Object.values(rank).some((m) => m !== '必須項目です'),
      );
    if (formatErrors) {
      setShowErrors(true);
      toast('error', '入力内容を確認してください');
      return;
    }

    // Phase G (old2 逐語): 必須未回答は confirm で部分保存可。
    const requiredMissing = countRequiredMissing();
    if (requiredMissing > 0) {
      setShowErrors(true);
      if (!confirm(`${requiredMissing}件の必須質問が未回答です。送信しますか？`)) {
        return;
      }
    }

    // form values → AiVerificationAnswerValue
    const perPostAnswers: Record<string, AiVerificationAnswerValue> = {};
    for (const q of perPostVisible) {
      const v = perPostValues[q.id];
      if (!v) continue;
      const av = toAnswerValue(v);
      if (av) perPostAnswers[q.id] = av;
    }
    const perClassificationAnswers: Record<string, Record<string, AiVerificationAnswerValue>> = {};
    for (const [rank, qs] of Object.entries(perClsVisibleByRank)) {
      const rankValues = perClsValues[rank] ?? {};
      const rankOut: Record<string, AiVerificationAnswerValue> = {};
      for (const q of qs) {
        const v = rankValues[q.id];
        if (!v) continue;
        const av = toAnswerValue(v);
        if (av) rankOut[q.id] = av;
      }
      if (Object.keys(rankOut).length > 0) perClassificationAnswers[rank] = rankOut;
    }

    // 検証対象が「自分の投稿か」(端末引き継ぎ元 UID も自分として扱う)。回答 doc に保存し、
    // 集計 trigger は post.authorUid から独立に再判定する (client 値は表示・簡易集計用)。
    const ownerUids = selfUids && selfUids.length > 0 ? selfUids : [uid];
    const isOwnPost = post.authorUid !== null && ownerUids.includes(post.authorUid);

    try {
      // computeCompleted (useSubmitVerification 内) が required 充足を判定し completed を保存する。
      await submit({
        assignmentSetId,
        postAuthorUid: post.authorUid,
        isOwnPost,
        templateId: template.id,
        templateVersion: template.version,
        aiSnapshot: {
          analyzedAt: post.aiAnalysisAnalyzedAt ?? '',
          settingsVersion: null,
        },
        perPostAnswers,
        perClassificationAnswers,
        freeComment: template.allowFreeComment
          ? freeComment.trim() === ''
            ? null
            : freeComment.trim()
          : null,
        perPostQuestions: template.perPostQuestions,
        perClassificationQuestions: template.perClassificationQuestions,
        totalRanks,
        existingSubmittedAt: existingVerification?.submittedAt ?? null,
      });

      // Phase G (old2 逐語): 連続回答。配分セット経由かつ next 解決 callback があれば
      // 次の未回答投稿へ自動遷移。末尾まで完了したら完了 alert を出して閉じる。
      if (assignmentSetId && findNextUnansweredPostId && onAdvanceToPost) {
        const next = await findNextUnansweredPostId(post.id);
        if (next) {
          toast('success', '検証回答を送信しました');
          onAdvanceToPost(next);
          return;
        }
        // セット内の最後まで → 完了 alert を出して一覧へ戻る (old2 逐語)。
        alert('このセットの全ての投稿への回答が完了しました。');
        onSubmitted();
        return;
      }

      toast('success', existingVerification ? '検証回答を更新しました' : '検証回答を送信しました');
      onSubmitted();
    } catch (err) {
      toast('error', `送信失敗: ${(err as Error).message}`);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {/* 投稿詳細モーダルに埋め込むときは二重表示になるため出さない (showPostContext=false)。 */}
      {showPostContext && (
        <header>
          <h3 className="text-lg font-bold text-slate-900">投稿「{post.title}」の検証</h3>
          {post.comment && (
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{post.comment}</p>
          )}
        </header>
      )}

      {/* Phase G (old2 逐語): 何を検証しているか見えるよう投稿写真 + AI 解析 + 投稿位置を表示。 */}
      {showPostContext && <PostContextView post={post} districtId={districtId} />}

      {/* Phase G (old2 VerificationFormPage L271-277 逐語): 回答済み (上書き) 警告 banner。 */}
      {existingVerification && (
        <div className="border border-amber-300 bg-amber-50 rounded p-2 text-xs text-amber-900">
          ✏️{' '}
          <strong>
            あなたは前回 {new Date(existingVerification.submittedAt).toLocaleString('ja-JP')}{' '}
            にこの投稿に回答済みです。
          </strong>
          再送信すると、前回の回答は<strong>新しい内容で上書き</strong>
          されます（旧回答は残りません）。
        </div>
      )}

      {showPostContext && <AiResultSummary post={post} />}

      {perPostVisible.length > 0 && (
        <section className="grid gap-3">
          <h4 className="text-sm font-bold text-slate-800">投稿全体について</h4>
          {perPostVisible.map((q, idx) => {
            const v = perPostValues[q.id];
            if (!v) return null;
            const err = showErrors ? perPostErrors[q.id] : undefined;
            return (
              <div key={q.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 shrink-0">
                    #{idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">
                      {q.label || '(質問文未入力)'}
                      {q.required && <span className="ml-1 text-danger-600">*</span>}
                    </p>
                    {q.helpText && (
                      <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">
                        {q.helpText}
                      </p>
                    )}
                  </div>
                </div>
                <QuestionWidget
                  question={q}
                  value={v}
                  onChange={(next) => setPerPost(q.id, next)}
                  disabled={isSubmitting}
                  fieldNamePrefix={`pp-${q.id}`}
                />
                {err && <p className="text-[11px] text-danger-700 mt-1.5">{err}</p>}
              </div>
            );
          })}
        </section>
      )}

      {totalRanks > 0 && Object.keys(perClsVisibleByRank).length > 0 && (
        <section className="grid gap-3">
          <h4 className="text-sm font-bold text-slate-800">分類行ごと</h4>
          {classifications.map((c) => {
            const qs = perClsVisibleByRank[c.rank];
            if (!qs || qs.length === 0) return null;
            const rankKey = String(c.rank);
            return (
              <div key={c.rank} className="border-l-2 border-slate-300 pl-3 grid gap-2">
                <p className="text-xs text-slate-500">
                  <strong>#{c.rank}</strong> {c.category_name ?? c.category_code ?? ''}
                  {c.assessment && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      {c.assessment}
                    </span>
                  )}
                </p>
                {qs.map((q, idx) => {
                  const v = perClsValues[rankKey]?.[q.id];
                  if (!v) return null;
                  const err = showErrors ? perClsErrors[rankKey]?.[q.id] : undefined;
                  return (
                    <div
                      key={`${rankKey}-${q.id}`}
                      className="border border-slate-200 rounded-lg p-3 bg-white"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 shrink-0">
                          #{idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800">
                            {q.label || '(質問文未入力)'}
                            {q.required && <span className="ml-1 text-danger-600">*</span>}
                          </p>
                          {q.helpText && (
                            <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">
                              {q.helpText}
                            </p>
                          )}
                        </div>
                      </div>
                      <QuestionWidget
                        question={q}
                        value={v}
                        onChange={(next) => setPerCls(rankKey, q.id, next)}
                        disabled={isSubmitting}
                        fieldNamePrefix={`pc-${rankKey}-${q.id}`}
                      />
                      {err && <p className="text-[11px] text-danger-700 mt-1.5">{err}</p>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
      )}

      {template.allowFreeComment && (
        <section>
          <label className="grid gap-1">
            <span className="text-sm font-bold text-slate-800">
              自由記述 <span className="text-[11px] text-slate-500">(任意、最大 500 字)</span>
            </span>
            <textarea
              value={freeComment}
              onChange={(e) => setFreeComment(e.target.value)}
              maxLength={500}
              rows={3}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
            />
            <small className="text-[10px] text-slate-400">{freeComment.length} / 500</small>
          </label>
        </section>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? '送信中…'
            : assignmentSetId && findNextUnansweredPostId && onAdvanceToPost
              ? '送信して次へ →'
              : existingVerification
                ? '更新する'
                : '送信する'}
        </Button>
      </div>
    </form>
  );
}
